// app-dropdown.component.ts
import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, TemplateRef, ViewChild, ViewContainerRef, computed, forwardRef, inject, input, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ConnectedPosition, Overlay, OverlayModule, OverlayRef } from '@angular/cdk/overlay';
import { PortalModule, TemplatePortal } from '@angular/cdk/portal';

/**
 * Dropdown option model.
 *
 * value: Internal value propagated to the Angular form control.
 * label: Human-readable text shown in the trigger and inside the dropdown panel.
 * disabled: Optional flag that prevents the option from being selected.
 */
export type AppDropdownOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

/**
 * Value type used by this component.
 *
 * Single-selection mode uses a string.
 * Multiple-selection mode uses a string array.
 */
export type AppDropdownValue = string | string[];

/**
 * Usage (Reactive Forms - single selection):
 * <dr-ops-stream-ui-app-dropdown
 *   formControlName="programId"
 *   [options]="programOptions()"
 *   placeholder="Program"
 *   floatingLabel="Program"
 *   openDirection="up"
 *   [panelMaxHeightRem]="18"
 *   size="normal"
 * ></dr-ops-stream-ui-app-dropdown>
 *
 * Usage (Reactive Forms - multiple selection):
 * <dr-ops-stream-ui-app-dropdown
 *   formControlName="programIds"
 *   [options]="programOptions()"
 *   [multiple]="true"
 *   placeholder="Programs"
 *   floatingLabel="Programs"
 *   openDirection="up"
 *   [panelMaxHeightRem]="18"
 *   size="normal"
 * ></dr-ops-stream-ui-app-dropdown>
 */
@Component({
  selector: 'dr-ops-stream-ui-app-dropdown',
  standalone: true,
  imports: [OverlayModule, PortalModule],
  templateUrl: './app-dropdown.component.html',
  styleUrls: ['./app-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppDropdownComponent),
      multi: true,
    },
  ],
})
export class AppDropdownComponent implements ControlValueAccessor, OnDestroy {
  // ---------------------------------------------
  // Inputs
  // ---------------------------------------------

  /** Optional id for accessibility (aria-labelledby) */
  readonly id = input<string>('');

  /** Placeholder shown when no value is selected */
  readonly placeholder = input<string>('Select…');

  /** Options rendered in the panel */
  readonly options = input<AppDropdownOption[]>([]);

  /**
   * Enables multiple-selection mode.
   *
   * false/default: the component behaves exactly like the original single-select dropdown.
   * true: the component keeps the panel open while selecting and propagates a string[] to the form control.
   */
  readonly multiple = input<boolean>(false);

  /**
   * Disabled
   */
  readonly disabled = input<boolean>(false);

  /** Whether to show a chevron icon */
  readonly showChevron = input<boolean>(true);
  readonly searchable = input<boolean>(false);

  /**
   * Where to open the panel:
   * - 'auto' tries down then up
   * - 'down' always below trigger
   * - 'up' always above trigger
   */
  readonly openDirection = input<'auto' | 'down' | 'up'>('auto');

  /**
   * Close when clicking outside the panel/trigger.
   */
  readonly closeOnOutsideClick = input<boolean>(true);

  /**
   * Floating label text.
   * Shown only when a value is selected (placeholder not shown).
   */
  readonly floatingLabel = input<string | null>(null);

  /** Panel max-height in rem */
  readonly panelMaxHeightRem = input<number>(18);

  /** Trigger height variants */
  readonly size = input<'small' | 'normal' | 'large'>('normal');

  /** Opacity to apply when disabled (defaults to 0.5) */
  readonly disabledOpacity = input<number>(0.5);

  /** Inline opacity applied to the trigger only while the component is disabled */
  readonly triggerOpacity = computed(() => {
    return this.isDisabled() ? this.disabledOpacity() : null;
  });

  /** Number of selected options in multiple mode */
  readonly selectedCount = computed(() => {
    if (!this.multiple()) {
      return 0;
    }

    return this._values().length;
  });

  /** Whether to show the selected-count badge in multiple mode */
  readonly showSelectedCountBadge = computed(() => {
    return this.multiple() && this.selectedCount() > 0;
  });

  // ---------------------------------------------
  // Template refs
  // ---------------------------------------------

  /** The panel template attached into the CDK overlay */
  @ViewChild('panelTpl') private panelTpl?: TemplateRef<unknown>;

  // Kept because it exists in your template; actual scrolling is performed via overlayRef.overlayElement.
  @ViewChild('panelEl') private panelEl?: ElementRef<HTMLElement>;

  // ---------------------------------------------
  // DI + overlay state
  // ---------------------------------------------

  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly vcr = inject(ViewContainerRef);
  private readonly overlay = inject(Overlay);

  private overlayRef?: OverlayRef;
  private portal?: TemplatePortal;

  /** Used to ignore the same click that opened the dropdown */
  private isOpening = false;

  // ---------------------------------------------
  // CVA (form integration) state
  // ---------------------------------------------

  /** Current search query typed in the panel (only used when searchable) */
  private readonly _search = signal<string>('');

  /** Options after applying the search filter (case-insensitive on label) */
  readonly filteredOptions = computed<AppDropdownOption[]>(() => {
    const all = this.options();
    if (!this.searchable()) return all;
    const q = this._search().trim().toLowerCase();
    if (!q) return all;
    return all.filter((o) => o.label.toLowerCase().includes(q));
  });

  /** Internal single-selection value used when multiple === false */
  private readonly _value = signal<string>('');

  /** Internal multiple-selection values used when multiple === true */
  private readonly _values = signal<string[]>([]);

  /** Disabled state received from Angular forms through ControlValueAccessor */
  private readonly _cvaDisabled = signal(false);

  /** Current selected value (string). Used by the original single-selection mode. */
  readonly value = computed(() => this._value());

  /** Current selected values (string[]). Used by the new multiple-selection mode. */
  readonly values = computed(() => this._values());

  /** Disabled state = form disabled OR disabled === true */
  readonly isDisabled = computed(() => this._cvaDisabled() || this.disabled());

  /** Whether overlay is currently attached */
  readonly isOpen = computed(() => !!this.overlayRef?.hasAttached());

  /** Label shown in the trigger when a single value is selected */
  readonly selectedLabel = computed(() => {
    const v = this._value();
    return this.options().find((o) => o.value === v)?.label ?? '';
  });

  /** Labels shown in the trigger when multiple values are selected */
  readonly selectedLabels = computed(() => {
    const selectedValues = new Set(this._values());
    return this.options()
      .filter((o) => selectedValues.has(o.value))
      .map((o) => o.label);
  });

  /**
   * Text shown in the trigger when the component has a selected value.
   *
   * Single-selection mode: selected option label.
   * Multiple-selection mode: selected option labels joined by comma.
   */
  readonly selectedDisplayLabel = computed(() => {
    if (!this.multiple()) return this.selectedLabel();
    return this.selectedLabels().join(', ');
  });

  /** True when no option is selected */
  readonly isPlaceholder = computed(() => {
    if (!this.multiple()) return this._value() === '';
    return this._values().length === 0;
  });

  /** Floating label shows only when a value is selected */
  readonly showFloatingLabel = computed(() => !!this.floatingLabel()?.trim() && !this.isPlaceholder());

  /** Inline max-height binding for the panel */
  readonly panelMaxHeightStyle = computed(() => `${this.panelMaxHeightRem()}rem`);

  /** Root modifier class for sizing */
  readonly sizeClass = computed(() => `select--${this.size()}`);

  private onChange: (value: AppDropdownValue) => void = () => {};
  private onTouched: () => void = () => {};

  // ---------------------------------------------
  // ControlValueAccessor implementation
  // ---------------------------------------------

  /**
   * Receives the value from Angular forms.
   *
   * Single-selection mode expects string | null.
   * Multiple-selection mode expects string[] | null.
   *
   * A small amount of defensive normalization is included so the component does not break if the form starts with null,
   * undefined, or a value type that does not match the current mode.
   */
  writeValue(value: AppDropdownValue | null): void {
    if (this.multiple()) {
      this._values.set(Array.isArray(value) ? value : value ? [value] : []);
      this._value.set('');
      return;
    }

    this._value.set(typeof value === 'string' ? value : '');
    this._values.set([]);
  }

  /** Stores the Angular forms callback used to propagate value changes */
  registerOnChange(fn: (value: AppDropdownValue) => void): void {
    this.onChange = fn;
  }

  /** Stores the Angular forms callback used to mark the control as touched */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /** Receives the disabled state from Angular forms */
  setDisabledState(isDisabled: boolean): void {
    this._cvaDisabled.set(isDisabled);
    if (isDisabled) this.close();
  }

  // ---------------------------------------------
  // Public UI actions
  // ---------------------------------------------

  /** Toggle panel open/closed */
  toggle(): void {
    if (this.isDisabled()) return;

    if (this.overlayRef?.hasAttached()) {
      this.close();
      this.onTouched();
      return;
    }

    this.open();
  }

  /** Close the panel (does not clear the selected value) */
  close(): void {
    this.overlayRef?.detach();
    this._search.set('');
  }

  /** Update the search query from the panel input */
  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement)?.value ?? '';
    this._search.set(value);
  }

  /**
   * Select an option and propagate its value via CVA.
   *
   * Single-selection mode:
   * - Replaces the current selected value.
   * - Propagates a string.
   * - Closes the panel, preserving the original component behavior.
   *
   * Multiple-selection mode:
   * - Toggles the clicked option on/off.
   * - Propagates a string[].
   * - Keeps the panel open so the user can continue selecting values.
   */
  select(opt: AppDropdownOption): void {
    if (this.isDisabled() || opt.disabled) return;

    if (this.multiple()) {
      this.toggleMultipleValue(opt.value);
      this.onTouched();
      return;
    }

    this._value.set(opt.value);
    this.onChange(opt.value);
    this.onTouched();
    this.close();
  }

  /**
   * Returns whether the provided option value is currently selected.
   * Used by the template for selected styling, aria-selected, and data-selected.
   */
  isOptionSelected(value: string): boolean {
    if (!this.multiple()) return this._value() === value;
    return this._values().includes(value);
  }

  ngOnDestroy(): void {
    this.overlayRef?.dispose();
  }

  // ---------------------------------------------
  // Overlay helpers
  // ---------------------------------------------

  private buildPositions(): ConnectedPosition[] {
    const down: ConnectedPosition = {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 6,
    };

    const up: ConnectedPosition = {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetY: -6,
    };

    const dir = this.openDirection();
    if (dir === 'down') return [down];
    if (dir === 'up') return [up];

    // auto: prefer down, fallback up
    return [down, up];
  }

  private open(): void {
    if (!this.panelTpl) return;

    const positionStrategy = this.overlay.position().flexibleConnectedTo(this.host.nativeElement).withPositions(this.buildPositions()).withFlexibleDimensions(false).withPush(true);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: false, // backdrop is not above dialogs; outsidePointerEvents() handles closing reliably
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      panelClass: 'app-dropdown-overlay-panel',
    });

    this.portal = new TemplatePortal(this.panelTpl, this.vcr);
    this.overlayRef.attach(this.portal);

    // Ignore the same click that opened the dropdown
    this.isOpening = true;
    queueMicrotask(() => (this.isOpening = false));

    // Close on outside click
    this.overlayRef.outsidePointerEvents().subscribe((event) => {
      if (!this.closeOnOutsideClick()) return;
      if (this.isOpening) return;

      const target = event.target as Node | null;
      if (!target) return;

      // Click inside trigger should not close
      if (this.host.nativeElement.contains(target)) return;

      // Click inside overlay panel should not close
      const overlayEl = this.overlayRef?.overlayElement;
      if (overlayEl && overlayEl.contains(target)) return;

      this.close();
      this.onTouched();
    });

    // ESC closes the panel
    this.overlayRef.keydownEvents().subscribe((e) => {
      if (e.key === 'Escape') {
        this.close();
        this.onTouched();
      }
    });

    // After attaching, auto-scroll to selected option
    this.scrollSelectedIntoView();
  }

  /**
   * Toggles one value in multiple-selection mode and propagates the full string[] value to Angular forms.
   *
   * If the value is already selected, it is removed.
   * If the value is not selected, it is appended to the end of the selected values array.
   */
  private toggleMultipleValue(value: string): void {
    const current = this._values();
    const exists = current.includes(value);
    const next = exists ? current.filter((v) => v !== value) : [...current, value];

    this._values.set(next);
    this.onChange(next);
  }

  /**
   * Scroll the overlay panel so the selected option is visible (and roughly centered).
   * This runs after attach() so the DOM exists.
   *
   * In single-selection mode, this scrolls to the selected option.
   * In multiple-selection mode, this scrolls to the first selected option.
   */
  private scrollSelectedIntoView(): void {
    requestAnimationFrame(() => {
      const overlayEl = this.overlayRef?.overlayElement;
      if (!overlayEl) return;

      const panel = overlayEl.querySelector<HTMLElement>('.select__panel');
      if (!panel) return;

      const selected = overlayEl.querySelector<HTMLElement>('[data-selected="true"]');
      if (!selected) return;

      const panelRect = panel.getBoundingClientRect();
      const selRect = selected.getBoundingClientRect();

      const above = selRect.top < panelRect.top;
      const below = selRect.bottom > panelRect.bottom;

      if (above || below) {
        const offsetTop = selected.offsetTop;
        const target = offsetTop - panel.clientHeight / 2 + selected.clientHeight / 2;
        panel.scrollTop = Math.max(0, target);
      }
    });
  }
}
